import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { selectUserRole } from "../../../../store/authSlice";
import {
  appApi,
  useGetNcmpComplaintsQuery,
  useGetNcmpSummaryQuery,
  useGetNcmpResolversQuery,
} from "../../../../store/appApi";
import {
  usePartnerCode,
  PartnerPicker,
  panelClass,
  fieldClass,
  btnGhost,
  Badge,
  fmtDate,
  COMPLAINT_STATUS_TABS,
  COMPLAINT_STATUS_TONE,
} from "./config";
import {
  resolveComplaintApi,
  addComplaintNoteApi,
  greenFlagComplaintApi,
} from "../../../../services/netplusComplaints";
import { uiToast } from "../customerStatus/ui";

const WHITE = { backgroundColor: "#fff", color: "#1f2937" };
const errMsg = (e) => e?.response?.data?.message || "Something went wrong.";

const NcmpList = () => {
  const dispatch = useDispatch();
  const isAdmin = useSelector(selectUserRole) === "super-admin";
  const [reasonDraft, setReasonDraft] = useState({});
  const [flagBusy, setFlagBusy] = useState("");
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [qLive, setQLive] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  const { data, isFetching } = useGetNcmpComplaintsQuery({
    partnerCode,
    status,
    q: qLive,
    from: fromDate,
    to: toDate,
    page,
    limit: 50,
  });
  const { data: summary } = useGetNcmpSummaryQuery({ partnerCode });
  const { data: resolversData } = useGetNcmpResolversQuery({ partnerCode });
  const resolvers = resolversData?.people || [];

  const rows = data?.complaints || [];
  const totalPages = data?.totalPages || 1;

  // Per-row draft: { [id]: { by: userId, checked: bool } }
  const [draft, setDraft] = useState({});
  const [savingId, setSavingId] = useState("");
  const initialDraft = (c) => ({
    by: c.resolvedBy?._id || c.resolvedBy || "",
    checked: c.status === "completed",
  });
  const getDraft = (c) => draft[c._id] || initialDraft(c);
  const setRow = (id, patch) =>
    setDraft((p) => {
      const c = rows.find((r) => r._id === id) || { _id: id };
      return { ...p, [id]: { ...(p[id] || initialDraft(c)), ...patch } };
    });

  const refresh = () =>
    dispatch(appApi.util.invalidateTags(["NetplusComplaints"]));

  const saveResolve = async (c) => {
    const d = getDraft(c);
    if (d.checked && !d.by) {
      uiToast({ type: "warning", title: "Pick the employee who resolved it." });
      return;
    }
    try {
      setSavingId(c._id);
      await resolveComplaintApi(
        c._id,
        d.checked
          ? { resolvedBy: d.by }
          : { resolved: false },
        partnerCode,
      );
      setDraft((p) => {
        const n = { ...p };
        delete n[c._id];
        return n;
      });
      refresh();
      uiToast({
        type: "success",
        title: d.checked ? "Marked resolved" : "Re-opened",
      });
    } catch (e) {
      uiToast({ type: "error", title: "Couldn't save", message: errMsg(e) });
    } finally {
      setSavingId("");
    }
  };

  const addReason = async (c) => {
    const text = (reasonDraft[c._id] || "").trim();
    if (!text) return;
    try {
      setFlagBusy(c._id);
      await addComplaintNoteApi(c._id, text, partnerCode);
      setReasonDraft((p) => ({ ...p, [c._id]: "" }));
      refresh();
      uiToast({ type: "success", title: "Reason logged" });
    } catch (e) {
      uiToast({ type: "error", title: "Couldn't save", message: errMsg(e) });
    } finally {
      setFlagBusy("");
    }
  };

  const toggleGreenFlag = async (c) => {
    try {
      setFlagBusy(c._id);
      await greenFlagComplaintApi(
        c._id,
        { greenFlagged: !c.greenFlagged },
        partnerCode,
      );
      refresh();
    } catch (e) {
      uiToast({ type: "error", title: "Failed", message: errMsg(e) });
    } finally {
      setFlagBusy("");
    }
  };

  const reopenedAfterResolve = (c) =>
    c.status === "open" &&
    (c.resolvedBy || (c.statusHistory || []).some((h) => h.from === "completed"));

  return (
    <>
      <PageTitle activeMenu="List" motherMenu="Complaints" />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        {[
          ["Total", summary?.total, "text-gray-900"],
          ["Open", summary?.open, "text-amber-600"],
          ["Completed", summary?.completed, "text-emerald-600"],
        ].map(([label, value, tone]) => (
          <div key={label} className={`${panelClass} px-4 py-3`}>
            <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
              {label}
            </div>
            <div className={`mt-1 text-2xl font-semibold tabular-nums ${tone}`}>
              {value ?? "—"}
            </div>
          </div>
        ))}
      </div>

      <section className={panelClass}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-gray-100 px-5 py-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {COMPLAINT_STATUS_TABS.map((t) => (
              <button
                key={t.key || "all"}
                onClick={() => {
                  setStatus(t.key);
                  setPage(1);
                }}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  status === t.key
                    ? "bg-cyan-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <form
            className="ml-auto flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setQLive(q.trim());
              setPage(1);
            }}
          >
            <PartnerPicker value={partnerCode} onChange={setPartnerCode} />
            <input
              type="date"
              className={fieldClass}
              style={{ backgroundColor: "#fff", color: "#1f2937" }}
              title="Complaint date from"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              type="date"
              className={fieldClass}
              style={{ backgroundColor: "#fff", color: "#1f2937" }}
              title="Complaint date to"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
            />
            <input
              className={`${fieldClass} w-full min-w-[10rem] sm:w-60`}
              style={{ backgroundColor: "#fff", color: "#1f2937" }}
              placeholder="Ticket / name / contact / text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button className={btnGhost} type="submit">
              Search
            </button>
          </form>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 text-left">Ticket No</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Contact No</th>
                <th className="px-4 py-3 text-left">Address</th>
                <th className="px-4 py-3 text-left">Complaint</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-left">Resolved by</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    No complaints.
                  </td>
                </tr>
              ) : (
                rows.map((c) => (
                  <tr key={c._id} className="align-top hover:bg-cyan-50/30">
                    <td className="px-4 py-2.5 font-semibold text-gray-800">
                      {c.ticketNumber}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {fmtDate(c.date)}
                    </td>
                    <td className="px-4 py-2.5 text-gray-700">{c.name || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {c.contactNumber || "—"}
                    </td>
                    <td className="px-4 py-2.5 max-w-[16rem] text-xs text-gray-500">
                      {c.address || "—"}
                    </td>
                    <td className="px-4 py-2.5 max-w-[18rem] text-xs text-gray-600">
                      {c.complaintText || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <Badge tone={COMPLAINT_STATUS_TONE[c.status]}>
                        {c.status}
                      </Badge>
                      {c.statusHistory?.length > 1 && (
                        <div className="mt-0.5 text-[10px] text-gray-400">
                          changed {fmtDate(
                            c.statusHistory[c.statusHistory.length - 1].at,
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {(() => {
                        const d = getDraft(c);
                        return (
                          <div className="flex flex-col gap-1.5">
                            <label className="flex items-center gap-1.5 text-xs text-gray-700">
                              <input
                                type="checkbox"
                                checked={d.checked}
                                onChange={(e) =>
                                  setRow(c._id, { checked: e.target.checked })
                                }
                              />
                              Resolved
                            </label>
                            <select
                              className={`${fieldClass} w-40`}
                              style={WHITE}
                              value={d.by}
                              disabled={!d.checked}
                              onChange={(e) =>
                                setRow(c._id, { by: e.target.value })
                              }
                            >
                              <option value="" style={WHITE}>
                                — employee —
                              </option>
                              {resolvers.map((p) => (
                                <option key={p._id} value={p._id} style={WHITE}>
                                  {p.name}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="w-fit rounded-lg bg-cyan-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-cyan-500 disabled:opacity-50"
                              disabled={savingId === c._id}
                              onClick={() => saveResolve(c)}
                            >
                              {savingId === c._id ? "…" : "Save"}
                            </button>
                            {c.resolvedBy && c.status === "completed" && (
                              <span className="text-[10px] text-emerald-700">
                                {c.resolvedBy.name || c.resolvedByName} ·{" "}
                                {fmtDate(c.resolvedAt)}
                              </span>
                            )}

                            {reopenedAfterResolve(c) && (
                              <div className="mt-1 rounded-lg border border-amber-200 bg-amber-50 p-1.5">
                                <div className="text-[10px] font-semibold text-amber-800">
                                  Reopened — needs a reason
                                </div>
                                {(c.employeeNotes || []).slice(-2).map((n, i) => (
                                  <div key={i} className="text-[10px] text-gray-600">
                                    {n.byName || "—"}: {n.text}
                                  </div>
                                ))}
                                <div className="mt-1 flex gap-1">
                                  <input
                                    className="w-32 rounded border border-gray-300 px-1.5 py-0.5 text-[11px]"
                                    style={WHITE}
                                    placeholder="genuine reason…"
                                    value={reasonDraft[c._id] || ""}
                                    onChange={(e) =>
                                      setReasonDraft((p) => ({
                                        ...p,
                                        [c._id]: e.target.value,
                                      }))
                                    }
                                  />
                                  <button
                                    className="rounded bg-amber-500 px-2 py-0.5 text-[11px] font-semibold text-slate-950 disabled:opacity-50"
                                    disabled={flagBusy === c._id}
                                    onClick={() => addReason(c)}
                                  >
                                    Add
                                  </button>
                                </div>
                                {isAdmin && (
                                  <label className="mt-1 flex items-center gap-1 text-[11px] font-medium text-emerald-800">
                                    <input
                                      type="checkbox"
                                      checked={!!c.greenFlagged}
                                      disabled={flagBusy === c._id}
                                      onChange={() => toggleGreenFlag(c)}
                                    />
                                    Green flag (no penalty)
                                  </label>
                                )}
                                {!isAdmin && c.greenFlagged && (
                                  <div className="mt-1 text-[10px] font-semibold text-emerald-700">
                                    ✓ Green-flagged
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-3 text-xs text-gray-500">
          <span>{data?.totalCount ?? 0} total</span>
          <div className="flex items-center gap-2">
            <button
              className={btnGhost}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              className={btnGhost}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default NcmpList;
